<template>
  <div ref="container" class="location-tracker-map__map" />
</template>

<script setup>
import maplibregl, { Marker } from 'maplibre-gl'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useLocationStore } from '../stores/locations.js'
import { calculateBottomHalfBoundingBox } from '../utils/boundingBox.js'

const props = defineProps({
  trackers: {
    type: Array,
    required: true,
  },
  maxZoom: {
    type: Number,
    default: 18,
  },
  padding: {
    type: Number,
    default: 50,
  },
  zoomDuration: {
    type: Number,
    default: 1000,
  },
  allowMove: {
    type: Boolean,
    default: true,
  },
  styleUrl: {
    type: String,
    default: 'https://tiles.openfreemap.org/styles/positron',
  },
  mapTheme: {
    type: Function,
    default: null,
  },
  regionBounds: {
    type: Array,
    default: null,
  },
})

const locations = useLocationStore()
const markers = ref({})
const hasMoved = ref(false)
let followResumeTimer

const container = ref()
const map = ref()

onMounted(() => {
  map.value = new maplibregl.Map({
    container: container.value,
    attributionControl: false,
    dragPan: props.allowMove,
    maxZoom: props.maxZoom,
    // Start on the campaign's region and zoom in from there once a location arrives
    bounds: props.regionBounds ?? undefined,
  })

  // Theming the style JSON before maplibre builds any layer means the plain style is never drawn
  map.value.setStyle(props.styleUrl, {
    transformStyle: (_previous, style) => props.mapTheme?.(style) ?? style,
  })
  map.value.on('load', createMarkers)

  map.value.on('dragend', scheduleFollowResume)

  map.value.on('zoomstart', (event) => {
    if (event.originalEvent) hasMoved.value = true
  })

  map.value.on('zoomend', (event) => {
    if (event.originalEvent) scheduleFollowResume()
  })
})

onUnmounted(() => clearTimeout(followResumeTimer))

const scheduleFollowResume = () => {
  hasMoved.value = true
  clearTimeout(followResumeTimer)
  followResumeTimer = setTimeout(() => {
    hasMoved.value = false
    centerMap()
  }, 10000)
}

const createMarkers = () => {
  Object.entries(markers.value).forEach((_, marker) => marker.remove())

  markers.value = Object.fromEntries(
    props.trackers.map((tracker) => {
      let el = undefined

      if (tracker.icon) {
        const [iconWidth, iconHeight] = (tracker.iconSize ?? '20x20').split('x')
        el = document.createElement('div')
        el.className = 'marker'
        el.style.backgroundImage = `url(${tracker.icon})`
        el.style.backgroundSize = 'contain'
        el.style.backgroundPosition = `tracker.anchor`
        el.style.width = `${iconWidth}px`
        el.style.height = `${iconHeight}px`
      }

      return [tracker.uuid, new Marker({ element: el, color: tracker.color, anchor: tracker.anchor })]
    })
  )

  updateMarkers()
}

const updateMarkers = () => {
  Object.entries(markers.value).forEach(([uuid, marker]) => {
    const currentLocation = locations.trackers?.[uuid]?.[0]

    if (!currentLocation) {
      return
    }

    const latLong = [parseFloat(currentLocation.long), parseFloat(currentLocation.lat)]

    marker.setLngLat(latLong).addTo(map.value)
  })

  centerMap()
}

const centerMap = () => {
  if (hasMoved.value) {
    return
  }

  const longLats = Object.values(markers.value)
    .map((marker) => marker.getLngLat())
    .filter(Boolean)
    .map((latLong) => [latLong.lng, latLong.lat])

  if (!longLats.length) {
    return
  }

  if (props.regionBounds) {
    const [[minLng, minLat], [maxLng, maxLat]] = calculateBottomHalfBoundingBox(longLats)
    // Offset so the middle of the pin graphic, not its tip, sits in the center of the screen
    const pinHeight = Number(props.trackers.find(({ icon }) => icon)?.iconSize?.split('x')[1]) || 0

    map.value.easeTo({
      center: [(minLng + maxLng) / 2, (minLat + maxLat) / 2],
      offset: [0, pinHeight / 2],
      duration: props.zoomDuration,
    })
    return
  }

  map.value.fitBounds(calculateBottomHalfBoundingBox(longLats), {
    maxZoom: props.maxZoom,
    padding: props.padding,
    duration: props.zoomDuration,
  })
}

watch(() => props.trackers, createMarkers)

watch(() => locations.trackers, updateMarkers, { deep: true })
</script>

<style>
/* Inside the shadow root the document's styles don't apply, so maplibre's CSS has to live here */
@import 'maplibre-gl/dist/maplibre-gl.css';
</style>

<style lang="scss">
.location-tracker-map__map {
  height: 100%;
  width: 100%;
}

.marker {
  display: block;
  background-size: contain;
  background-repeat: no-repeat;
}
</style>
